import { el } from "./dom.js";
import { ASSET_CREDITS } from "../game/assetCredits.js";
import { audio } from "../game/audio.js";
import { api } from "../net/api.js";
import { postEnabled, setPostEnabled, forceHighGfx, setForceHighGfx, lowSpec, setLowSpec } from "../game/renderer.js";
import { gfxQuality, setGfxQuality, type GfxQuality } from "../game/gfxConfig.js";
import { isTouchDevice } from "../game/touch.js";
import {
  DEFAULT_BINDINGS,
  loadBindings,
  saveBindings,
  loadSensitivity,
  saveSensitivity,
  loadBehavior,
  saveBehavior,
  loadPadBinds,
  savePadBinds,
  padButtonName,
  PAD_BIND_DEFAULTS,
  activeGamepad,
  CH_DEFAULTS,
  type Action,
  type PadAction,
} from "../game/input.js";
import { applyCrosshairSettings, CH_COLORS } from "../game/hud.js";

const PAD_ACTION_LABELS: Record<PadAction, string> = {
  fire: "Fire",
  ads: "Aim (ADS)",
  jump: "Jump",
  crouch: "Crouch",
  sprint: "Sprint",
  reload: "Reload",
  use: "Use / Interact / Ride",
  grenade: "Grenade",
  melee: "Quick Melee (knife)",
  heal: "Health Shot",
  taunt: "Taunt",
  stoke: "Stoke (Burnout wager)",
};

/** Wait for the next FRESH pad button press (all buttons must release first —
 *  otherwise the ✕ press that clicked the row instantly binds itself). The
 *  D-pad hat axis is decoded to virtual buttons 12-15. Times out in 8s. */
function capturePadButton(onDone: (i: number | null) => void): () => void {
  let armed = false;
  const started = performance.now();
  const timer = window.setInterval(() => {
    if (performance.now() - started > 8000) { stop(); onDone(null); return; }
    const gp = activeGamepad();
    if (!gp) return;
    const pressed: number[] = [];
    gp.buttons.forEach((b, i) => { if (b.pressed) pressed.push(i); });
    const hat = gp.axes[9] ?? 3.3;
    if (hat <= 2) {
      const st = Math.round((hat + 1) / (2 / 7));
      if (st === 7 || st === 0 || st === 1) pressed.push(12);
      if (st >= 1 && st <= 3) pressed.push(15);
      if (st >= 3 && st <= 5) pressed.push(13);
      if (st >= 5 && st <= 7) pressed.push(14);
    }
    if (!pressed.length) { armed = true; return; }
    if (armed) { stop(); onDone(pressed[0]); }
  }, 40);
  const onKey = (e: KeyboardEvent): void => { if (e.code === "Escape") { e.preventDefault(); stop(); onDone(null); } };
  window.addEventListener("keydown", onKey, true);
  const stop = (): void => { window.clearInterval(timer); window.removeEventListener("keydown", onKey, true); };
  return stop;
}

const ACTION_LABELS: Record<Action, string> = {
  grapple: "Grappling Hook",
  forward: "Move Forward",
  back: "Move Back",
  left: "Move Left",
  right: "Move Right",
  jump: "Jump / Mantle",
  crouch: "Crouch / Sneak",
  sprint: "Sprint",
	  reload: "Reload",
	  use: "Use / Interact",
	  melee: "Quick Melee (knife)",
	  grenade: "Grenade",
	  stoke: "Stoke (Burnout wager)",
	  scoreboard: "Scoreboard",
	  thirdPerson: "Third-Person (hold)",
	};

function keyName(code: string): string {
  return code
    .replace(/^Key/, "")
    .replace(/^Digit/, "")
    .replace(/^Arrow/, "")
    .replace("ControlLeft", "L-Ctrl")
    .replace("ControlRight", "R-Ctrl")
    .replace("ShiftLeft", "L-Shift")
    .replace("ShiftRight", "R-Shift")
    .replace("AltLeft", "L-Alt")
    .replace("AltRight", "R-Alt")
    .replace("Space", "Space");
}

/** Settings: rebindable keys, sensitivity, behavior toggles, FOV, volumes. */
export function settingsScreen(onBack: () => void): HTMLElement {
  const screen = el("div", { class: "screen overlay" });
  const panel = el("div", { class: "panel", style: "min-width:520px" });

  const bindings = loadBindings();
  const sens = loadSensitivity();
  const behavior = loadBehavior();
  // persist to the ACCOUNT too (debounced) so tuning follows the user across
  // devices; guests silently keep localStorage only
  let syncT = 0;
  const syncToAccount = (): void => {
    window.clearTimeout(syncT);
    syncT = window.setTimeout(() => {
      let volumes: unknown = undefined;
      try { volumes = JSON.parse(localStorage.getItem("neonpurge.volumes") ?? "null") ?? undefined; } catch { /* keep */ }
      let soundVols: unknown = undefined;
      try { soundVols = JSON.parse(localStorage.getItem("neonpurge.soundvols") ?? "null") ?? undefined; } catch { /* keep */ }
      api.saveSettings({ sensitivity: sens, behavior, ...(volumes ? { volumes } : {}), ...(soundVols ? { soundVols } : {}) });
    }, 600);
  };

  // Only ONE key/pad rebind capture may be armed at a time. A capture arms a
  // listener on `window` (keyboard) or a polling interval (pad); if the panel
  // goes away while a capture is armed, that listener otherwise survives and the
  // NEXT keystroke / pad button pressed ANYWHERE is swallowed and silently
  // rebound. Leaving Settings does not reliably run `onBack`: closePause()
  // (match.ts) removes the panel with a raw `.remove()`, and Escape can close it
  // too. So: track the active capture, cancel it when another starts or Done is
  // pressed, and watch `screen.isConnected` to catch every other close path.
  let cancelCapture: (() => void) | null = null;
  let captureWatch = 0;
  const armCapture = (cancel: () => void): void => {
    cancelCapture?.(); // a re-arm cancels the prior (single concurrent capture)
    cancelCapture = cancel;
    window.clearInterval(captureWatch);
    captureWatch = window.setInterval(() => { if (!screen.isConnected) cancelCapture?.(); }, 200);
  };
  const disarmCapture = (cancel: () => void): void => {
    if (cancelCapture !== cancel) return; // a newer capture already owns the slot
    cancelCapture = null;
    window.clearInterval(captureWatch);
    captureWatch = 0;
  };

  // --- key bindings
  // Track each row's button so "Reset Keys" can refresh the labels directly —
  // re-inserting the pre-built nodes via render() does NOT rewrite their text
  // (each label is set once at creation and only in that button's own handler),
  // exactly the reason the controller sibling keeps `padRowBtns` below.
  const bindRowBtns: Array<{ action: Action; btn: HTMLElement }> = [];
  const bindRows = (Object.keys(ACTION_LABELS) as Action[]).map((action) => {
    const btn = el("button", { class: "bind-btn", text: keyName(bindings[action][0] ?? "—") });
    bindRowBtns.push({ action, btn });
    btn.onclick = () => {
      btn.textContent = "press a key…";
      btn.classList.add("listening");
      const cancel = (): void => {
        window.removeEventListener("keydown", onKey, true);
        btn.classList.remove("listening");
        btn.textContent = keyName(bindings[action][0] ?? "—");
        disarmCapture(cancel);
      };
      const onKey = (e: KeyboardEvent): void => {
        // panel already gone (bypass close within the watchdog window): release
        // cleanly WITHOUT eating the key or rebinding.
        if (!screen.isConnected) { cancel(); return; }
        e.preventDefault();
        if (e.code !== "Escape") {
          bindings[action] = [e.code];
          saveBindings(bindings);
        }
        cancel();
      };
      window.addEventListener("keydown", onKey, true);
      armCapture(cancel);
    };
    return el("div", { class: "set-row" }, el("label", { text: ACTION_LABELS[action] }), btn);
  });

  // --- controller bindings (press-to-rebind, D-pad hat supported)
  const padBinds = loadPadBinds();
  const padRowBtns: Array<{ action: PadAction; btn: HTMLElement }> = [];
  const padRows = (Object.keys(PAD_ACTION_LABELS) as PadAction[]).map((action) => {
    const btn = el("button", { class: "bind-btn", text: padButtonName(padBinds[action]) });
    padRowBtns.push({ action, btn });
    btn.onclick = () => {
      btn.textContent = "press a pad button…";
      btn.classList.add("listening");
      const stop = capturePadButton((i) => {
        btn.classList.remove("listening");
        if (i !== null) {
          padBinds[action] = i;
          savePadBinds(padBinds);
        }
        btn.textContent = padButtonName(padBinds[action]);
        disarmCapture(cancel); // resolved on its own — release the slot + watchdog
      });
      const cancel = (): void => {
        stop(); // kills the polling interval (and its Escape listener)
        btn.classList.remove("listening");
        btn.textContent = padButtonName(padBinds[action]);
        disarmCapture(cancel);
      };
      armCapture(cancel);
    };
    return el("div", { class: "set-row" }, el("label", { text: PAD_ACTION_LABELS[action] }), btn);
  });
  const resetPadBinds = el("button", { class: "ghost", text: "Reset Controller" });
  resetPadBinds.onclick = () => {
    Object.assign(padBinds, PAD_BIND_DEFAULTS);
    savePadBinds(padBinds);
    for (const r of padRowBtns) r.btn.textContent = padButtonName(padBinds[r.action]);
  };

  const resetBinds = el("button", { class: "ghost", text: "Reset Keys" });
  resetBinds.onclick = () => {
    Object.assign(bindings, structuredClone(DEFAULT_BINDINGS));
    saveBindings(bindings);
    // Refresh the row labels in place (mirrors resetPadBinds) — render() only
    // re-inserts the same button nodes, so it would leave the pre-reset key
    // names on screen until Settings is closed and reopened.
    for (const r of bindRowBtns) r.btn.textContent = keyName(bindings[r.action][0] ?? "—");
  };

  // --- sliders
  // Readout precision follows the STEP so a slider never lies about what it does:
  // the old fixed `.toFixed(2)` collapsed the whole useful mouse-sensitivity
  // range (0.0002–0.02, step 0.0001) to a constant "0.00", and printed integer
  // sliders as "90.00" / "10.00". `pct`/`deg` add units where the number alone
  // (a 0–1 volume, a field of view) reads worse than a labelled one.
  const stepFmt = (step: number): ((v: number) => string) => {
    const s = String(step);
    const dot = s.indexOf(".");
    const dec = dot < 0 ? 0 : s.length - dot - 1;
    return (v) => v.toFixed(dec);
  };
  const pct = (v: number): string => `${Math.round(v * 100)}%`;
  const deg = (v: number): string => `${v.toFixed(0)}°`;
  const slider = (
    label: string,
    min: number,
    max: number,
    step: number,
    value: number,
    onInput: (v: number) => void,
    fmt: (v: number) => string = stepFmt(step),
  ): HTMLElement => {
    const valEl = el("span", { class: "slider-val", text: fmt(value) });
    const input = el("input", {
      type: "range",
      min: String(min),
      max: String(max),
      step: String(step),
      value: String(value),
    });
    input.oninput = () => {
      const v = Number(input.value);
      valEl.textContent = fmt(v);
      onInput(v);
    };
    return el("div", { class: "set-row" }, el("label", { text: label }), input, valEl);
  };

  const toggle = (label: string, value: boolean, onChange: (v: boolean) => void): HTMLElement => {
    const box = el("input", { type: "checkbox", style: "width:auto" });
    if (value) box.setAttribute("checked", "checked");
    box.onchange = () => onChange(box.checked);
    return el("div", { class: "set-row" }, el("label", { text: label }), box);
  };

  const select = (
    label: string,
    opts: { value: string; text: string }[],
    value: string,
    onChange: (v: string) => void,
  ): HTMLElement => {
    const sel = el("select", { style: "flex:1;min-width:0" }) as HTMLSelectElement;
    for (const o of opts) {
      const opt = el("option", { value: o.value, text: o.text }) as HTMLOptionElement;
      if (o.value === value) opt.selected = true;
      sel.append(opt);
    }
    sel.onchange = () => onChange(sel.value);
    return el("div", { class: "set-row" }, el("label", { text: label }), sel);
  };

  // --- per-sound mixer: every recorded sample gets its own volume slider.
  // Click a sound's name to PREVIEW it at the adjusted level.
  const mixerPanel = el("div", { style: "display:none;max-height:280px;overflow:auto;border:1px solid #2a3242;border-radius:6px;padding:0.4rem 0.6rem;margin:0.3rem 0" });
  const mixerBtn = el("button", { class: "ghost", text: "🎚 Per-sound volumes ▸", style: "margin-top:0.4rem" });
  let mixerBuilt = false;
  const prettyName = (n: string): string => (n.startsWith("voice_") ? `🎙 ${n.slice(6)}` : `🔊 ${n}`);
  const buildMixer = (): void => {
    const names = audio.sampleNames().sort((a, b) => {
      const av = a.startsWith("voice_") ? 1 : 0, bv = b.startsWith("voice_") ? 1 : 0;
      return av - bv || a.localeCompare(b);
    });
    const rows: HTMLElement[] = [];
    let lastGroup = "";
    for (const name of names) {
      const group = name.startsWith("voice_") ? "ANNOUNCER VOICE" : "SOUND EFFECTS";
      if (group !== lastGroup) { rows.push(el("h3", { text: group, style: "margin:0.5rem 0 0.2rem;opacity:0.7;font-size:0.8em" })); lastGroup = group; }
      const pctVol = (v: number): string => `${Math.round(v * 100)}%`;
      const valEl = el("span", { class: "slider-val", text: pctVol(audio.soundVol(name)) });
      const input = el("input", { type: "range", min: "0", max: "2", step: "0.05", value: String(audio.soundVol(name)) });
      input.oninput = () => {
        const v = Number(input.value);
        valEl.textContent = pctVol(v);
        audio.setSoundVol(name, v);
        syncToAccount();
      };
      const nameBtn = el("button", { class: "ghost", text: prettyName(name), style: "min-width:11em;text-align:left;padding:0 0.4em" });
      nameBtn.onclick = () => { audio.onGesture(); audio.preview(name); }; // hear it at the set level
      rows.push(el("div", { class: "set-row" }, nameBtn, input, valEl));
    }
    const resetBtn = el("button", { class: "ghost", text: "Reset to calibrated defaults" });
    resetBtn.onclick = () => { audio.resetSoundVols(); syncToAccount(); mixerBuilt = false; buildMixer(); };
    rows.push(el("div", { class: "set-row", style: "justify-content:flex-end" }, resetBtn));
    mixerPanel.replaceChildren(...rows);
    mixerBuilt = true;
  };
  mixerBtn.onclick = () => {
    const open = mixerPanel.style.display === "none";
    if (open && !mixerBuilt) buildMixer();
    mixerPanel.style.display = open ? "block" : "none";
    mixerBtn.textContent = open ? "🎚 Per-sound volumes ▾" : "🎚 Per-sound volumes ▸";
  };

  // Live crosshair preview — a real .crosshair on a dark tile, centred in the
  // box. It inherits the :root CSS vars set by applyCrosshairSettings below, so
  // it updates the instant any crosshair control changes (no per-frame spread
  // here, so it uses the CSS resting gap).
  const chPreview = el(
    "div",
    { style: "position:relative;height:88px;margin:0.3rem 0;border:1px solid #2a3242;border-radius:6px;background:radial-gradient(circle at 50% 45%, #223049, #05060a);overflow:hidden" },
    el("div", { class: "crosshair" },
      el("div", { class: "ch-dot" }),
      el("div", { class: "ch-arm ch-t" }),
      el("div", { class: "ch-arm ch-b" }),
      el("div", { class: "ch-arm ch-l" }),
      el("div", { class: "ch-arm ch-r" }),
    ),
  );
  // Push saved crosshair settings onto :root now, so the preview (and, from the
  // menu, the next match's HUD) reflect them before anything is changed.
  applyCrosshairSettings(behavior);

  const content = el("div", {});
  function render(): void {
    content.replaceChildren(
      el("h2", { text: "Mouse & Aim" }),
      slider("Sensitivity", 0.0002, 0.02, 0.0001, sens.mouse, (v) => {
        sens.mouse = v;
        saveSensitivity(sens);
        syncToAccount();
      }),
      slider("Touch look sensitivity ×", 0.4, 3, 0.1, sens.touchMul ?? 1, (v) => {
        sens.touchMul = v;
        saveSensitivity(sens);
        syncToAccount();
      }),
      slider("ADS Sensitivity ×", 0.2, 1.5, 0.05, sens.adsMult, (v) => {
        sens.adsMult = v;
        saveSensitivity(sens);
        syncToAccount();
      }),
      toggle("Invert Y", sens.invertY, (v) => {
        sens.invertY = v;
        saveSensitivity(sens);
        syncToAccount();
      }),

      el("h2", { class: "mt1", text: "Gameplay" }),
      toggle("ADS: hold (off = toggle)", behavior.adsHold, (v) => {
        behavior.adsHold = v;
        saveBehavior(behavior);
        syncToAccount();
      }),
      toggle("Crouch: hold (off = toggle)", behavior.crouchHold, (v) => {
        behavior.crouchHold = v;
        saveBehavior(behavior);
        syncToAccount();
      }),
      toggle("Auto-aim (aim assist) — shows a ? over you", behavior.autoAim, (v) => {
        behavior.autoAim = v;
        saveBehavior(behavior);
        syncToAccount();
      }),
      slider("Vibration ×", 0, 2.5, 0.1, behavior.rumble ?? 1, (v) => {
        behavior.rumble = v;
        saveBehavior(behavior);
        syncToAccount();
      }),
      slider("FOV", 70, 110, 1, behavior.fov, (v) => {
        behavior.fov = v;
        saveBehavior(behavior);
        syncToAccount();
      }, deg),
      // Touch-only: hide the glass buttons/joystick to play with a controller.
      // Defaults on; the match applies it live (match.ts settings-close).
      ...(isTouchDevice() ? [toggle("On-screen buttons (off for controller)", behavior.onScreenButtons !== false, (v) => {
        behavior.onScreenButtons = v;
        saveBehavior(behavior);
        syncToAccount();
      })] : []),

      el("h2", { class: "mt1", text: "Accessibility & help" }),
      select("Colour-blind markers", [
        { value: "off", text: "Off (green zombies / yellow players)" },
        { value: "deut", text: "Deuteranopia (red-green)" },
        { value: "prot", text: "Protanopia (red-green)" },
        { value: "trit", text: "Tritanopia (blue-yellow)" },
      ], behavior.colorblind ?? "off", (v) => {
        behavior.colorblind = v as NonNullable<typeof behavior.colorblind>;
        saveBehavior(behavior); syncToAccount();
      }),
      toggle("Subtitles for the announcer", behavior.subtitles !== false, (v) => {
        behavior.subtitles = v;
        saveBehavior(behavior); syncToAccount();
      }),
      toggle("Beginner tips + mode card at match start", behavior.tips !== false, (v) => {
        behavior.tips = v;
        saveBehavior(behavior); syncToAccount();
      }),
      (() => {
        const b = el("button", { class: "ghost", text: "↺ Show the first-time tips again", style: "margin-top:0.3rem" });
        b.onclick = () => {
          try {
            for (let i = localStorage.length - 1; i >= 0; i--) {
              const k = localStorage.key(i);
              if (k && k.startsWith("neonpurge.seen.")) localStorage.removeItem(k);
            }
          } catch { /* private mode */ }
          b.textContent = "✔ tips reset";
        };
        return b;
      })(),

      el("h2", { class: "mt1", text: "Crosshair" }),
      chPreview,
      select("Style", [
        { value: "neon", text: "Neon (default)" },
        { value: "cross", text: "Cross" },
        { value: "dot", text: "Dot" },
        { value: "crossdot", text: "Cross + Dot" },
      ], behavior.chStyle ?? CH_DEFAULTS.chStyle, (v) => {
        behavior.chStyle = v as NonNullable<typeof behavior.chStyle>;
        saveBehavior(behavior); syncToAccount(); applyCrosshairSettings(behavior);
      }),
      select("Color", Object.keys(CH_COLORS).map((k) => ({ value: k, text: k[0].toUpperCase() + k.slice(1) })),
        behavior.chColor ?? CH_DEFAULTS.chColor, (v) => {
          behavior.chColor = v;
          saveBehavior(behavior); syncToAccount(); applyCrosshairSettings(behavior);
        }),
      slider("Size (arm length)", 2, 16, 1, behavior.chSize ?? CH_DEFAULTS.chSize, (v) => {
        behavior.chSize = v;
        saveBehavior(behavior); syncToAccount(); applyCrosshairSettings(behavior);
      }),
      slider("Thickness", 1, 5, 1, behavior.chThick ?? CH_DEFAULTS.chThick, (v) => {
        behavior.chThick = v;
        saveBehavior(behavior); syncToAccount(); applyCrosshairSettings(behavior);
      }),
      slider("Gap", 0, 16, 1, behavior.chGap ?? CH_DEFAULTS.chGap, (v) => {
        behavior.chGap = v;
        saveBehavior(behavior); syncToAccount(); applyCrosshairSettings(behavior);
      }),
      toggle("Outline (dark edge for contrast)", behavior.chOutline ?? CH_DEFAULTS.chOutline, (v) => {
        behavior.chOutline = v;
        saveBehavior(behavior); syncToAccount(); applyCrosshairSettings(behavior);
      }),

      el("h2", { class: "mt1", text: "Audio" }),
      slider("Master", 0, 1, 0.05, audio.volumes.master, (v) => { audio.setVolumes({ master: v }); syncToAccount(); }, pct),
      slider("SFX", 0, 1, 0.05, audio.volumes.sfx, (v) => { audio.setVolumes({ sfx: v }); syncToAccount(); }, pct),
      slider("Announcement vocals", 0, 1.5, 0.05, audio.volumes.voice ?? 1, (v) => { audio.setVolumes({ voice: v }); syncToAccount(); }, pct),
      toggle("Voice announcer", behavior.voiceAnnounce !== false, (v) => {
        behavior.voiceAnnounce = v;
        audio.voiceEnabled = v;
        saveBehavior(behavior);
        syncToAccount();
      }),
      slider("Music", 0, 1, 0.05, audio.volumes.music, (v) => { audio.setVolumes({ music: v }); syncToAccount(); }, pct),
      mixerBtn,
      mixerPanel,

      el("h2", { class: "mt1", text: "Video" }),
      select("Graphics quality", [
        { value: "auto", text: "Auto (adapts to your PC)" },
        { value: "low", text: "Low — fastest" },
        { value: "medium", text: "Medium" },
        { value: "high", text: "High — sharp shadows, AO, HDR sky" },
      ], gfxQuality(), (v) => {
        setGfxQuality(v as GfxQuality);
        // An explicit Medium/High is the player saying "this PC can take it".
        // It must also clear the sticky auto PERFORMANCE MODE flag: lowSpec()
        // always wins over the preset, so on a once-flagged machine choosing
        // High silently changed nothing — no photo surfaces, no shadows, no
        // realistic models (owner, 2026-11-04: "i dont see any update").
        if (v === "medium" || v === "high") setLowSpec(false);
      }),
      el("div", { class: "hint", text: "Applies from the next match. Every preset still lowers cost on its own if frames run late. F7 opens the tuning panel." }),
      ...(lowSpec() ? [el("div", { class: "hint", text: "⚠ Performance mode is ON for this PC (auto-detected after slow frames): photo textures and shadows are off (the real 3D models stay on). Pick Medium or High above, or turn on Full effects, to turn it off." })] : []),
      toggle("Full effects — never auto-reduce", forceHighGfx(), (v) => {
        setForceHighGfx(v);
      }),
      toggle("Bloom + scanlines (neon maps)", postEnabled(), (v) => setPostEnabled(v)),

      el("h2", { class: "mt1", text: "Key Bindings" }),
      ...bindRows,
      resetBinds,

      el("h2", { class: "mt1", text: "Controller Bindings" }),
      el("div", { class: "hint", text: "Click an action, then press the pad button. Driving keeps L2/L1 for climb/dive; R1 hops ground rides." }),
      ...padRows,
      resetPadBinds,

      // CC-BY / MIT assets REQUIRE visible attribution — this is where it lives
      ...(ASSET_CREDITS.length ? [
        el("h2", { class: "mt1", text: "Credits" }),
        el("div", { class: "hint", text: "Third-party models and textures used in NEON PURGE:" }),
        ...ASSET_CREDITS.map((c) => el("div", { class: "hint", style: "text-align:left;margin:0.25rem 0" },
          el("b", { text: `${c.what}: ` }),
          el("span", { text: `${c.source} — ${c.license} ` }),
          el("span", { style: "opacity:0.7", text: c.url }))),
      ] : []),
    );
  }
  render();

  const back = el("button", { class: "primary wide mt1", text: "Done" });
  back.onclick = () => { cancelCapture?.(); onBack(); };

  panel.append(
    el("div", { class: "brand", text: "SETTINGS" }),
    content,
    back,
  );
  screen.append(panel);
  return screen;
}
